import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";

import { Sidebar } from "../Sidebar";

import SummaryData from "../SummaryData";
import SalesData from "../SalesData";
import CardSearchInputs from "../CardSearchInputs";
import ChangeIdeaLog from "../ChangeIdeaLog";
import ChangeLog from "../ChangeLog";
import DatabaseDesign from "../DatabaseDesign";
import SessionManager from "../SessionManager";
import PurchaseRequests from "../modules/purchaserequests/PurchaseRequests";
import DevGuide from "../DevGuide";
import TemplateCreator from "../modules/template_generator/TemplateCreator";
import TestTableGenerator from "../TestTableGenerator";
import Testfinal from "../Testfinal";
import PurchaseOrders from "../modules/purchaseorders/PurchaseOrders";
import TrainerInventory from "../modules/trainerinventory/TrainerInventory";
import PlayableCardIntake from "../modules/playableintake/PlayableCardIntake";
import AIOperator from "../modules/aioperator/AIOperator";
import TestDateTable from "../TestDateTable";
import SharedSourceViewer from "../SharedSourceViewer";
import AgentPatchManager from "../AgentPatchManager";
import ListingApp from "../modules/ListingApp";
import ConsignmentsDashboard from "../modules/consignments/ConsignmentsDashboard";
import ListTemplatesApp from "../modules/ListTemplates";
import DeveloperOverlay from "../DeveloperOverlay";
import ListingImagesPanel from "../modules/ListingApp/ListingImagesPanel";
import OffersDashboard from "../modules/offers/OffersDashboard";
import CardsTableView from "../modules/cards/CardsTableView";
import AppDesigner from "../modules/AgentApp/views/AppDesigner";
import ProfitsDashboard from "../modules/profits/ProfitsDashboard";
import ActionsDashboard from "../modules/actions/ActionsDashboard";
import EbayIntelligenceDashboard from "../modules/ebayintelligence";
import RepricingDashboard from "../modules/repricing";
import DevToolsRoot from "../modules/devtools"; // <-- Dev IDE
import UserManagementPanel from "../modules/user_management/panels/UserManagementPanel";
import MyTimePage from "../modules/user_management/MyTimePage";
import StrategyPlanner from "../modules/strategy_planner/StrategyPlanner";
import { UserAuthProvider, useUserAuth } from "../modules/user_management/UserAuthContext";

import {
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Dialog,
  Alert,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";

// Shipping may be intentionally absent from a target environment during release sync.
// An unconditional import prevents the entire app from compiling in that case.
// Resolve the real module when present; make absence explicit rather than
// silently replacing its functionality or bringing in Shipping code via FC252.
function ShippingNotInstalled() {
  return (
    <Alert severity="warning" sx={{ m: 2 }}>
      Shipping has not been deployed to this environment. Install the Shipping
      module before using the Shipping screens here.
    </Alert>
  );
}

// CRA uses webpack: this narrowly scoped, empty-safe context allows builds
// with or without src/components/modules/shipping/index.js.
const shippingModuleContext = typeof require.context === "function"
  ? require.context("../modules", true, /^\.\/shipping\/index\.js$/)
  : null;
const shippingIndexPath = "./shipping/index.js";
const ShippingModule = shippingModuleContext && shippingModuleContext.keys().includes(shippingIndexPath)
  ? shippingModuleContext(shippingIndexPath).default
  : ShippingNotInstalled;

// ---------------------------------------------------------
// Container configuration (high-level app groups)
// ---------------------------------------------------------
const CONTAINERS = {
  aioperator: {
    label: "AI Operator",
    modules: [{ value: "playablecardresearch", label: "Playable Card Research" }],
  },
  actions: {
    label: "Actions",
    modules: [{ value: "actions", label: "Actions" }],
  },
  sourcing: {
    label: "Sourcing",
    modules: [
      { value: "sessions", label: "Sessions" },
      { value: "purchaserequests", label: "Purchase Requests" },
      { value: "purchaseorders", label: "Purchase Orders" },
      { value: "trainerinventory", label: "Trainer Inventory" },
      { value: "playableintake", label: "Playable Card Intake" },
      { value: "consignments", label: "Consignments" },
      { value: "cardsearch", label: "Card Search" },
      { value: "cards", label: "Cards" },
    ],
  },
  listing: {
    label: "Preparing Listings",
    modules: [
      { value: "listingapp", label: "Listings" },
      { value: "listtemplates", label: "List Templates" },
      { value: "offers", label: "Offers" },
      { value: "ebayintelligence", label: "eBay Intelligence" },
      { value: "repricing", label: "Repricing" },
      { value: "imageuploader", label: "Image Uploader" },
      { value: "templatecreator", label: "Template Creator" },
      { value: "testtablegenerator", label: "TestTableGenerator" },
      { value: "Testfinal", label: "Testfinal" },
      { value: "testdatetable", label: "TestDateTable" },
    ],
  },
  profits: {
    label: "Profits",
    modules: [
      { value: "actions", label: "Actions" },
      { value: "ProfitsDashboard", label: "ProfitsDashboard" },
      { value: "summary", label: "Summary Data" },
      { value: "sales", label: "Sales Data" },
    ],
  },
  shipping: {
    label: "Shipping",
    modules: [
      { value: "shippingqueue", label: "Queue" },
      { value: "shippingpacking", label: "Packing Station" },
      { value: "shippingreview", label: "Review" },
    ],
  },
  dev: {
    label: "App Development",
    modules: [
      { value: "devtools", label: "Dev IDE" },
      { value: "AppDesigner", label: "App Designer" },
      { value: "strategyplanner", label: "Strategy Planner" },
      { value: "userlogin", label: "User Login" },
      { value: "mytime", label: "My Time" },
      { value: "changeideas", label: "Change Ideas" },
      { value: "changelog", label: "Change Log" },
      { value: "databasedesign", label: "Database Design" },
      { value: "devguide", label: "Dev Guide" },
      { value: "agentpatch", label: "Agent Patch" },
      { value: "sharedsourceviewer", label: "Shared Source Viewer" },
      { value: "developeroverlay", label: "Developer Overlay" },
    ],
  },
};

// helper to find which container a module belongs to
const findContainerForModule = (moduleValue) => {
  for (const [key, container] of Object.entries(CONTAINERS)) {
    if (container.modules.some((m) => m.value === moduleValue)) {
      return key;
    }
  }
  return null;
};

function DevToolsAuthBridge({ token }) {
  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const originalFetch = window.fetch.bind(window);
    const interceptorId = axios.interceptors.request.use((config) => {
      const url = config?.url || "";
      if (typeof url === "string" && url.includes("/api/dev-ide") && token) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    });

    window.fetch = (input, init = {}) => {
      const url = typeof input === "string" ? input : input?.url || "";
      if (!url.includes("/api/dev-ide")) {
        return originalFetch(input, init);
      }

      if (input instanceof Request) {
        const requestHeaders = new Headers(input.headers || {});
        if (!requestHeaders.has("Authorization")) {
          requestHeaders.set("Authorization", `Bearer ${token}`);
        }
        const nextRequest = new Request(input, {
          ...init,
          headers: requestHeaders,
        });
        return originalFetch(nextRequest);
      }

      const headers = new Headers(init?.headers || {});
      if (!headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return originalFetch(input, {
        ...init,
        headers,
      });
    };

    return () => {
      window.fetch = originalFetch;
      axios.interceptors.request.eject(interceptorId);
    };
  }, [token]);

  return null;
}

function DevToolsAccessGate() {
  const { isAuthenticated, loading, user, token } = useUserAuth();

  if (loading) {
    return <div>Checking Dev IDE access...</div>;
  }

  if (!isAuthenticated) {
    return (
      <div>
        <div
          style={{
            marginBottom: "16px",
            padding: "12px 16px",
            borderRadius: "12px",
            background: "#fff7ed",
            border: "1px solid #fdba74",
            color: "#9a3412",
            fontWeight: 600,
          }}
        >
          Dev IDE is locked. Please log in to access this module.
        </div>
        <UserManagementPanel />
      </div>
    );
  }

  return (
    <>
      <DevToolsAuthBridge token={token} />
      <DevToolsRoot authUser={user} />
    </>
  );
}

export default function DashboardApp() {
  const [selectedContainer, setSelectedContainer] = useState("sourcing");
  const [selectedApp, setSelectedApp] = useState("sessions");

  // remember last module per container
  const [lastModuleByContainer, setLastModuleByContainer] = useState({});

  // Cmd/Ctrl+K palette state
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState("");
  const paletteInputRef = useRef(null);

  // top search value (drives paletteQuery)
  const [topSearch, setTopSearch] = useState("");

  // controls whether the top search input is shown vs collapsed button
  const [searchActive, setSearchActive] = useState(false);

  const clearSearchUI = () => {
    setTopSearch("");
    setPaletteQuery("");
    setPaletteOpen(false);
    setSearchActive(false);
  };

  const updateModuleQuery = (moduleValue, params = {}) => {
    const searchParams = new URLSearchParams(window.location.search || "");
    searchParams.set("module", moduleValue);
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        searchParams.delete(key);
      } else {
        searchParams.set(key, String(value));
      }
    });
    const nextUrl = `${window.location.pathname}?${searchParams.toString()}${window.location.hash || ""}`;
    window.history.replaceState({}, "", nextUrl);
  };

  // select module + keep container + memory in sync
  const selectModule = (moduleValue) => {
    setSelectedApp(moduleValue);

    const containerKey = findContainerForModule(moduleValue);
    if (containerKey) {
      setSelectedContainer(containerKey);
      setLastModuleByContainer((prev) => ({
        ...prev,
        [containerKey]: moduleValue,
      }));
    }

    // after navigating, close/collapse search UI
    clearSearchUI();
  };

  const handleChange = (event) => {
    const value = event.target.value;
    selectModule(value);
  };

  // Build a flat list for searching
  const allModules = useMemo(() => {
    return Object.entries(CONTAINERS).flatMap(([containerKey, container]) =>
      container.modules.map((m) => ({
        value: m.value,
        label: m.label,
        containerKey,
        containerLabel: container.label,
      }))
    );
  }, []);

  const filteredModules = useMemo(() => {
    const q = paletteQuery.trim().toLowerCase();
    if (!q) return allModules;

    return allModules.filter((m) => {
      return (
        m.label.toLowerCase().includes(q) ||
        m.value.toLowerCase().includes(q) ||
        m.containerLabel.toLowerCase().includes(q)
      );
    });
  }, [allModules, paletteQuery]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search || "");
    const moduleParam = params.get("module");
    if (moduleParam && allModules.some((module) => module.value === moduleParam)) {
      selectModule(moduleParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allModules]);

  useEffect(() => {
    const onNavigateModule = (event) => {
      const moduleValue = event?.detail?.module;
      if (!moduleValue || !allModules.some((module) => module.value === moduleValue)) return;
      updateModuleQuery(moduleValue, event.detail.params || {});
      selectModule(moduleValue);
    };
    window.addEventListener("app:navigate-module", onNavigateModule);
    return () => window.removeEventListener("app:navigate-module", onNavigateModule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allModules]);

  // Cmd/Ctrl+K listener
  useEffect(() => {
    const onKeyDown = (e) => {
      const isK = e.key && e.key.toLowerCase() === "k";
      if ((e.ctrlKey || e.metaKey) && isK) {
        e.preventDefault();
        setSearchActive(true);
        setPaletteOpen(true);
        setPaletteQuery("");
        setTopSearch("");
      }
      if (e.key === "Escape") {
        clearSearchUI();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Focus palette input when opened
  useEffect(() => {
    if (paletteOpen) {
      setTimeout(() => paletteInputRef.current?.focus(), 0);
    }
  }, [paletteOpen]);

  const renderContent = () => {
    if (
      selectedApp === "userlogin" ||
      selectedApp === "mytime" ||
      selectedApp === "devtools"
    ) {
      return (
        <UserAuthProvider>
          {selectedApp === "userlogin" ? (
            <UserManagementPanel />
          ) : selectedApp === "mytime" ? (
            <MyTimePage />
          ) : (
            <DevToolsAccessGate />
          )}
        </UserAuthProvider>
      );
    }

    switch (selectedApp) {
      case "summary":
        return <SummaryData />;
      case "sales":
        return <SalesData />;
      case "cardsearch":
        return <CardSearchInputs />;
      case "changeideas":
        return <ChangeIdeaLog />;
      case "changelog":
        return <ChangeLog />;
      case "databasedesign":
        return <DatabaseDesign />;
      case "sessions":
        return <SessionManager />;
      case "purchaserequests":
        return <PurchaseRequests session_id={26} />;
      case "purchaseorders":
        return <PurchaseOrders />;
      case "trainerinventory":
        return <TrainerInventory />;
      case "playableintake":
        return <PlayableCardIntake />;
      case "playablecardresearch":
        return <AIOperator />;
      case "consignments":
        return <ConsignmentsDashboard />;
      case "cards":
        return <CardsTableView />;
      case "listingapp":
        return <ListingApp />;
      case "listtemplates":
        return <ListTemplatesApp />;
      case "offers":
        return <OffersDashboard />;
      case "ebayintelligence":
        return <EbayIntelligenceDashboard />;
      case "repricing":
        return <RepricingDashboard />;
      case "actions":
        return <ActionsDashboard />;
      case "ProfitsDashboard":
        return <ProfitsDashboard />;
      case "shippingqueue":
        return <ShippingModule initialView="queue" />;
      case "shippingpacking":
        return <ShippingModule initialView="packing" />;
      case "shippingreview":
        return <ShippingModule initialView="review" />;
      case "actions":
        return <ActionsDashboard />;
      case "AppDesigner":
        return <AppDesigner />;
      case "strategyplanner":
        return <StrategyPlanner />;
      case "imageuploader":
        return <ListingImagesPanel />;
      case "devguide":
        return <DevGuide />;
      case "templatecreator":
        return <TemplateCreator />;
      case "agentpatch":
        return <AgentPatchManager />;
      case "testtablegenerator":
        return <TestTableGenerator />;
      case "Testfinal":
        return <Testfinal />;
      case "testdatetable":
        return <TestDateTable />;
      case "sharedsourceviewer":
        return <SharedSourceViewer />;
      case "developeroverlay":
        return <DeveloperOverlay />;
      default:
        return <div>Select a mini application from the sidebar.</div>;
    }
  };

  const currentModules = CONTAINERS[selectedContainer]?.modules || [];

  return (
    <div className="flex h-screen flex-col">
      <Sidebar
        selectedContainer={selectedContainer}
        onSelectContainer={(containerKey) => {
          setSelectedContainer(containerKey);

          const modules = CONTAINERS[containerKey]?.modules || [];
          const remembered = lastModuleByContainer[containerKey];
          const nextModule =
            remembered && modules.some((m) => m.value === remembered)
              ? remembered
              : modules[0]?.value;

          if (nextModule) {
            setSelectedApp(nextModule);
          }
        }}
        searchActive={searchActive}
        searchValue={topSearch}
        onSearchOpen={() => {
          setSearchActive(true);
          setPaletteOpen(true);
          setPaletteQuery(topSearch);
        }}
        onSearchClose={clearSearchUI}
        onSearchChange={(e) => {
          const val = e.target.value;
          setTopSearch(val);
          setPaletteQuery(val);
          if (!paletteOpen) setPaletteOpen(true);
        }}
        onSearchKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (filteredModules.length > 0) {
              selectModule(filteredModules[0].value);
            }
          }
          if (e.key === "Escape") {
            clearSearchUI();
          }
        }}
      />

      <Dialog
        open={paletteOpen}
        onClose={clearSearchUI}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "rgba(49,46,129,1)" }}>
          Search modules
        </DialogTitle>
        <DialogContent>
          <Select
            value=""
            displayEmpty
            sx={{ display: "none" }}
            readOnly
          />
          <TextField
            inputRef={paletteInputRef}
            fullWidth
            size="small"
            placeholder="Type to search…"
            value={paletteQuery}
            onChange={(e) => {
              setPaletteQuery(e.target.value);
              setTopSearch(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && filteredModules.length > 0) {
                selectModule(filteredModules[0].value);
              }
              if (e.key === "Escape") {
                clearSearchUI();
              }
            }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <List sx={{ mt: 1, maxHeight: 360, overflow: "auto" }}>
            {filteredModules.slice(0, 25).map((m) => (
              <ListItemButton
                key={m.value}
                onClick={() => selectModule(m.value)}
                sx={{
                  borderRadius: 2,
                  "&:hover": { bgcolor: "rgba(99,102,241,0.08)" },
                }}
              >
                <ListItemText
                  primary={m.label}
                  secondary={m.containerLabel}
                  primaryTypographyProps={{ sx: { fontWeight: 600 } }}
                />
              </ListItemButton>
            ))}
            {filteredModules.length === 0 && (
              <ListItemText
                primary="No matches"
                secondary="Try a different search term."
                sx={{ px: 2, py: 1.5 }}
              />
            )}
          </List>
        </DialogContent>
      </Dialog>

      <div className="flex-1 p-4 overflow-auto bg-slate-50">
        {/* Module selector panel (now top-most) */}
        <div className="rounded-2xl bg-white shadow-sm border border-slate-200 p-3">
          {/* Mobile dropdown */}
          <div className="block md:hidden">
            <FormControl fullWidth>
              <InputLabel>Module</InputLabel>
              <Select value={selectedApp} label="Module" onChange={handleChange}>
                {Object.entries(CONTAINERS).flatMap(([containerKey, container]) => [
                  <ListSubheader key={`${containerKey}-sub`} disableSticky>
                    {container.label}
                  </ListSubheader>,
                  ...container.modules.map((mod) => (
                    <MenuItem key={mod.value} value={mod.value}>
                      {mod.label}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
          </div>

          {/* Desktop tabs */}
          <div className="hidden md:block">
            <Tabs
              value={selectedApp}
              onChange={(e, newValue) => selectModule(newValue)}
              textColor="primary"
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTabs-flexContainer": { gap: 1 },
                "& .MuiTab-root": { borderRadius: 2, minHeight: 42 },
              }}
            >
              {currentModules.map((mod) => {
                const active = selectedApp === mod.value;
                return (
                  <Tab
                    key={mod.value}
                    value={mod.value}
                    label={mod.label}
                    sx={{
                      fontWeight: active ? 700 : 500,
                      opacity: active ? 1 : 0.75,
                      textTransform: "none",
                      minHeight: 44,
                      border: active
                        ? "1px solid rgba(99,102,241,0.25)"
                        : "1px solid transparent",
                      bgcolor: active ? "rgba(99,102,241,0.10)" : "transparent",
                      "&:hover": {
                        bgcolor: active
                          ? "rgba(99,102,241,0.14)"
                          : "rgba(99,102,241,0.06)",
                      },
                    }}
                  />
                );
              })}
            </Tabs>
          </div>
        </div>

        {/* Module content panel */}
        <div className="mt-4 rounded-2xl bg-white shadow-sm border border-slate-200 p-4">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
